import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ArrowLeft, ArrowRight, Check, Clock, Mail, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SiteFooter } from "@/components/SiteFooter";
import { QUESTIONS, OUTCOMES, SUPPORT_EMAIL } from "@/lib/assessmentData";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.3 } };

const TopBar = () => {
  const nav = useNavigate();
  return (
    <header className="glass fixed top-0 inset-x-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button onClick={() => nav("/")} data-testid="assessment-home-btn" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-7 h-7 rounded-sm bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
          <span className="font-display font-extrabold text-lg tracking-tight">TARDIGRADE</span>
        </button>
      </div>
    </header>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, optional }) => (
  <label className="block">
    <span className="block mb-2 text-[var(--txt-secondary)]" style={{ fontSize: "15px" }}>{label}{optional && <span className="text-[var(--txt-muted)]"> (optional)</span>}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[var(--bg-elevated)] border border-white/15 px-4 py-3 text-base text-white outline-none focus:border-white transition-colors" />
  </label>
);

const DetailsStep = ({ details, setDetails, onNext }) => {
  const set = (k) => (v) => setDetails({ ...details, [k]: v });
  const valid = details.name.trim() && details.role.trim() && /^\S+@\S+\.\S+$/.test(details.email.trim());
  return (
    <motion.div key="details" {...fade} data-testid="assessment-details-step">
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-5">Is a resilience simulation right for your organisation?</h1>
      <p className="text-[var(--txt-secondary)] leading-relaxed mb-3" style={{ fontSize: "18px" }}>This short assessment isn't a test and there are no scores. It simply helps us both understand whether a Tardigrade simulation would be a useful next step for your organisation.</p>
      <p className="flex items-center gap-2 text-[var(--txt-muted)] mb-10" style={{ fontSize: "15px" }}><Clock className="w-4 h-4" /> Takes up to 2 minutes · 8 multiple-choice questions</p>
      <div className="grid sm:grid-cols-2 gap-5 mb-10">
        <Field label="Your name" value={details.name} onChange={set("name")} testid="assessment-name-input" />
        <Field label="Your role" value={details.role} onChange={set("role")} testid="assessment-role-input" />
        <Field label="Email address" type="email" value={details.email} onChange={set("email")} testid="assessment-email-input" />
        <Field label="Phone number" type="tel" value={details.phone} onChange={set("phone")} testid="assessment-phone-input" optional />
        <div className="sm:col-span-2"><Field label="Organisation" value={details.organisation} onChange={set("organisation")} testid="assessment-org-input" optional /></div>
      </div>
      <button data-testid="assessment-start-btn" disabled={!valid} onClick={onNext}
        className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 font-medium hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        Begin <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

const Progress = ({ index }) => (
  <div className="mb-10" data-testid="assessment-progress">
    <div className="flex justify-between text-[var(--txt-muted)] mb-3 font-mono" style={{ fontSize: "13px" }}>
      <span data-testid="assessment-progress-label">Question {index + 1} of {QUESTIONS.length}</span>
      <span>{Math.round((index / QUESTIONS.length) * 100)}%</span>
    </div>
    <div className="h-1 bg-white/10"><motion.div className="h-full bg-[var(--urgent)]" animate={{ width: `${(index / QUESTIONS.length) * 100}%` }} transition={{ duration: 0.4 }} /></div>
  </div>
);

const Option = ({ label, selected, multi, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`w-full text-left flex items-center gap-4 px-5 py-4 border transition-colors ${selected ? "border-white bg-white/10" : "border-white/15 hover:border-white/40 hover:bg-white/5"}`}>
    <span className={`w-5 h-5 shrink-0 flex items-center justify-center border ${multi ? "" : "rounded-full"} ${selected ? "bg-white border-white" : "border-white/40"}`}>
      {selected && <Check className="w-3.5 h-3.5 text-black" />}
    </span>
    <span style={{ fontSize: "17px" }}>{label}</span>
  </button>
);

const QuestionStep = ({ index, answers, setAnswer, onBack, onNext, submitting }) => {
  const q = QUESTIONS[index];
  const value = answers[q.key];
  const last = index === QUESTIONS.length - 1;
  const pick = (opt) => {
    if (q.multi) return setAnswer(q.key, value?.includes(opt) ? value.filter((v) => v !== opt) : [...(value || []), opt]);
    setAnswer(q.key, opt);
    if (!last) setTimeout(() => onNext(index), 250);
  };
  const ready = q.multi ? value?.length > 0 : !!value;
  return (
    <motion.div key={q.key} {...fade} data-testid={`assessment-question-${index + 1}`}>
      <Progress index={index} />
      <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-snug mb-3">{q.q}</h2>
      {q.hint && <p className="text-[var(--txt-muted)] mb-2" style={{ fontSize: "15px" }}>{q.hint}</p>}
      <div className="space-y-3 mt-8">
        {q.options.map((o, i) => <Option key={o} label={o} multi={q.multi} selected={q.multi ? value?.includes(o) : value === o} onClick={() => pick(o)} testid={`assessment-option-${index + 1}-${i}`} />)}
      </div>
      <div className="flex items-center justify-between mt-10">
        <button data-testid="assessment-back-btn" onClick={onBack} className="inline-flex items-center gap-2 text-[var(--txt-secondary)] hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button data-testid="assessment-next-btn" disabled={!ready || submitting} onClick={() => onNext(index)}
          className="inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 font-medium hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing your result</> : <>{last ? "See my result" : "Next"} <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </motion.div>
  );
};

const ResultView = ({ result }) => {
  const nav = useNavigate();
  const o = OUTCOMES[result.outcome];
  return (
    <motion.div key="result" {...fade} data-testid="assessment-result">
      <div className="text-[var(--txt-muted)] font-mono mb-3" style={{ fontSize: "13px" }}>YOUR RESULT</div>
      <h1 data-testid="assessment-result-title" className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-8">{o.title}</h1>
      <div className="space-y-4 text-white leading-relaxed mb-10" style={{ fontSize: "18px" }}>{o.paragraphs.map((p) => <p key={p}>{p}</p>)}</div>
      <div className="border-l-2 border-[var(--urgent)] pl-5 mb-10" data-testid="assessment-result-reasons">
        <div className="text-[var(--txt-secondary)] font-medium mb-3" style={{ fontSize: "16px" }}>Why we reached this result</div>
        <ul className="space-y-2 text-[var(--txt-secondary)]" style={{ fontSize: "16px" }}>{result.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
      {o.resources && (
        <div className="mb-10" data-testid="assessment-resources">
          <div className="font-display font-bold text-xl mb-2">Helpful resources</div>
          <p className="text-[var(--txt-secondary)] mb-5" style={{ fontSize: "15px" }}>Four free, fillable guides you can complete on screen or print.</p>
          <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {o.resources.map((r, i) => (
              <a key={r.file} href={r.file} download target="_blank" rel="noreferrer" data-testid={`assessment-resource-${i}`}
                className="group bg-[var(--bg-surface)] hover:bg-[var(--bg-elevated)] transition-colors p-5 flex items-center justify-between gap-3">
                <span className="flex items-center gap-3"><FileText className="w-4 h-4 text-[var(--txt-muted)] shrink-0" />{r.title}</span>
                <span className="flex items-center gap-1.5 font-mono text-[var(--txt-secondary)] group-hover:text-white shrink-0 transition-colors" style={{ fontSize: "12px" }}><Download className="w-3.5 h-3.5" /> PDF</span>
              </a>
            ))}
          </div>
        </div>
      )}
      {o.closing && <p className="text-[var(--txt-secondary)] italic leading-relaxed" style={{ fontSize: "17px" }}>{o.closing}</p>}
      {o.cta && (
        <a data-testid="assessment-cta-btn" href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(o.cta.subject)}`}
          className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 font-medium hover:bg-gray-200 transition-colors">
          <Mail className="w-4 h-4" /> {o.cta.label}
        </a>
      )}
      <div className="mt-12">
        <button data-testid="assessment-close-btn" onClick={() => nav("/")}
          className="text-[var(--txt-muted)] hover:text-[var(--txt-secondary)] underline-offset-4 hover:underline transition-colors" style={{ fontSize: "14px" }}>
          Close
        </button>
      </div>
    </motion.div>
  );
};

export default function Assessment() {
  const [step, setStep] = useState(-1);
  const [details, setDetails] = useState({ name: "", role: "", email: "", phone: "", organisation: "" });
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const sending = useRef(false);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setSubmitting(true);
    try {
      const r = await api.post("/assessments", { ...details, email: details.email.trim(), answers });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again."); }
    sending.current = false;
    setSubmitting(false);
  };
  const next = (from) => (from === QUESTIONS.length - 1 ? submit() : setStep(from + 1));

  return (
    <div className="min-h-screen bg-[var(--bg-base)] text-white flex flex-col">
      <TopBar />
      <main className="flex-1 w-full max-w-3xl mx-auto px-6 pt-32 pb-24">
        <AnimatePresence mode="wait">
          {result ? <ResultView result={result} />
            : step < 0 ? <DetailsStep details={details} setDetails={setDetails} onNext={() => setStep(0)} />
            : <QuestionStep index={step} answers={answers} setAnswer={setAnswer} onBack={() => setStep((s) => s - 1)} onNext={next} submitting={submitting} />}
        </AnimatePresence>
      </main>
      <SiteFooter />
    </div>
  );
}
